import React from 'react';
import Icon from '../../../components/AppIcon';

const AchievementDisplay = ({ achievements }) => {
  const achievementCategories = [
    {
      id: 'collection',
      name: 'Collection Milestones',
      icon: 'Package',
      achievements: achievements?.filter(a => a?.category === 'collection')
    },
    {
      id: 'value',
      name: 'Portfolio Growth',
      icon: 'TrendingUp',
      achievements: achievements?.filter(a => a?.category === 'value')
    },
    {
      id: 'community',
      name: 'Community Recognition',
      icon: 'Users',
      achievements: achievements?.filter(a => a?.category === 'community')
    },
    {
      id: 'expertise',
      name: 'Expertise & Knowledge',
      icon: 'Award',
      achievements: achievements?.filter(a => a?.category === 'expertise')
    }
  ];

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'legendary': return 'text-warning border-warning/20 bg-warning/10';
      case 'epic': return 'text-purple-400 border-purple-400/20 bg-purple-400/10';
      case 'rare': return 'text-primary border-primary/20 bg-primary/10';
      case 'common': return 'text-muted-foreground border-border bg-muted/20';
      default: return 'text-muted-foreground border-border bg-muted/20';
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 100) return 'bg-success';
    if (progress >= 75) return 'bg-primary';
    if (progress >= 50) return 'bg-warning';
    return 'bg-muted-foreground';
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Trophy" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Achievements</h2>
            <p className="text-muted-foreground">Your collecting milestones and recognition</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-foreground">
            {achievements?.filter(a => a?.unlocked)?.length}
          </div>
          <div className="text-sm text-muted-foreground">
            of {achievements?.length} unlocked
          </div>
        </div>
      </div>
      <div className="space-y-8">
        {achievementCategories?.map((category) => (
          <div key={category?.id}>
            <div className="flex items-center space-x-2 mb-4">
              <Icon name={category?.icon} size={18} className="text-primary" />
              <h3 className="text-lg font-semibold text-foreground">{category?.name}</h3>
              <div className="text-sm text-muted-foreground">
                ({category?.achievements?.filter(a => a?.unlocked)?.length}/{category?.achievements?.length})
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {category?.achievements?.map((achievement) => (
                <div
                  key={achievement?.id}
                  className={`p-4 border rounded-lg transition-all duration-300 premium-hover ${
                    achievement?.unlocked
                      ? `${getRarityColor(achievement?.rarity)} opacity-100`
                      : 'border-border bg-muted/10 opacity-60'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      achievement?.unlocked ? 'bg-current/10' : 'bg-muted/20'
                    }`}>
                      <Icon 
                        name={achievement?.icon} 
                        size={24} 
                        className={achievement?.unlocked ? 'text-current' : 'text-muted-foreground'} 
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className={`font-semibold truncate ${
                          achievement?.unlocked ? 'text-current' : 'text-muted-foreground'
                        }`}>
                          {achievement?.name}
                        </h4>
                        {achievement?.unlocked && (
                          <Icon name="Check" size={14} className="text-success flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {achievement?.description}
                      </p>
                      
                      {achievement?.unlocked ? (
                        <div className="flex items-center space-x-2 text-xs">
                          <Icon name="Calendar" size={12} />
                          <span className="text-muted-foreground">
                            Unlocked {achievement?.unlockedDate}
                          </span>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">Progress</span>
                            <span className="font-medium">{achievement?.progress}%</span>
                          </div>
                          <div className="w-full bg-muted/30 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(achievement?.progress)}`}
                              style={{ width: `${achievement?.progress}%` }}
                            />
                          </div>
                          {achievement?.requirement && (
                            <p className="text-xs text-muted-foreground">
                              {achievement?.requirement}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {/* Recent Achievements */}
      <div className="mt-8 pt-6 border-t border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4">Recent Achievements</h3>
        <div className="space-y-3">
          {achievements?.filter(a => a?.unlocked && a?.recent)?.slice(0, 3)?.map((achievement) => (
              <div key={achievement?.id} className="flex items-center space-x-3 p-3 bg-muted/20 rounded-lg">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getRarityColor(achievement?.rarity)}`}>
                  <Icon name={achievement?.icon} size={20} />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-foreground">{achievement?.name}</div>
                  <div className="text-sm text-muted-foreground">
                    Unlocked {achievement?.unlockedDate}
                  </div>
                </div>
                <Icon name="Sparkles" size={16} className="text-primary" />
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default AchievementDisplay;